import React, { useState } from 'react';
import { Alert, ProgressBar } from 'react-bootstrap';
import { useJobStatus } from '../context/JobStatusContext';

/** A finished job stays on screen this long after it ended (unless dismissed), so its outcome is not missed. */
const SHOW_FINISHED_MS = 5 * 60 * 1000;

const KIND_LABELS = { SYNC: 'Sync', RECOMPUTE: 'Recompute' } as const;

/** Shows the running sync or recompute with its progress, and how the last one ended. */
const JobBanner: React.FC = () => {
  const { job } = useJobStatus();
  const [dismissed, setDismissed] = useState<string | null>(null);
  if (!job) return null;

  if (job.state === 'RUNNING') {
    const known = job.current !== null && job.total !== null && job.total > 0;
    return (
      <Alert variant="info" className="job-banner py-2 mb-0" role="status">
        <div className="d-flex flex-wrap justify-content-between gap-2">
          <strong>{KIND_LABELS[job.kind]} running</strong>
          <span>
            {job.step}
            {known ? ` (${job.current} of ${job.total})` : '…'}
          </span>
        </div>
        <ProgressBar
          className="mt-1"
          style={{ height: 6 }}
          animated={!known}
          striped={!known}
          now={known ? Math.round(((job.current ?? 0) / (job.total ?? 1)) * 100) : 100}
        />
      </Alert>
    );
  }

  const endedAt = job.finishedAt ? Date.parse(job.finishedAt) : 0;
  if (dismissed === job.startedAt || Date.now() - endedAt > SHOW_FINISHED_MS) return null;
  return (
    <Alert
      variant={job.state === 'DONE' ? 'success' : 'danger'}
      className="job-banner py-2 mb-0"
      dismissible
      onClose={() => setDismissed(job.startedAt)}
      role="status"
    >
      <strong>{KIND_LABELS[job.kind]} {job.state === 'DONE' ? 'finished' : 'failed'}.</strong>{' '}
      {job.message}
    </Alert>
  );
};

export default JobBanner;
